/**
 * Slice 9.6 — RequireAuth wrapper.
 *
 * Wraps a route. If not logged in: redirects to /login while preserving
 * the destination so the user lands back where they were after login.
 * If logged in but missing a required role: shows a 403-style message
 * (NOT a redirect — that would loop).
 */

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth, type RoleCode } from './AuthContext';

interface RequireAuthProps {
  children: ReactNode;
  /** If provided, user must have ANY of these roles. */
  anyRole?: RoleCode[];
}

export function RequireAuth({ children, anyRole }: RequireAuthProps) {
  const { user, roles, isLoading, hasRole, mustChangePassword } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <span className="text-ink-400 text-sm">Loading…</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (anyRole && anyRole.length > 0 && !hasRole(...anyRole)) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12 text-center">
        <h1 className="text-2xl font-display text-ink mb-2">Access denied</h1>
        <p className="text-ink-500">
          This page requires one of the following roles:{' '}
          <code className="bg-ink-50 px-1 rounded text-sm">{anyRole.join(', ')}</code>.
        </p>
        <p className="text-ink-400 text-xs mt-4">
          You are signed in as <code>{user.email}</code> with{' '}
          <code>{roles.join(', ') || 'no roles'}</code>. Ask an administrator to grant access.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
